"use client";

import { CalendarIcon, ChevronLeft, ChevronRight, Circle, Globe } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { addDays, format, isAfter, isBefore, parse, startOfWeek } from "date-fns";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { DayPicker } from "react-day-picker";

import TemplateApplyButton from "./templates/TemplateApplyButton";

import { CoachMarker } from "@/components/CoachMarker";
import { SidebarTrigger } from "@/components/ui/sidebar.tsx";
import { WorkspaceSwitcher } from "@/features/workspaces/components/widget/workspace-switcher.tsx";
import { Button, buttonVariants } from "@/components/ui/button.tsx";
import { getTimezoneAbbreviation } from "@/features/calendar/utils/timezone.ts";
import { useCalendarStore } from "@/features/calendar/store/useCalendarStore";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import useUserStore from "@/features/auth/store/useUserStore";

export default function CalendarHeader() {
  const { t } = useTranslation(["timezones", "common", "help"]);
  const { calendarData, shownDays, dateRange, setDateRange } = useCalendarStore();
  const isMobile = useIsMobile();
  const redirect = useNavigate();
  const displayTimezone = calendarData?.displayTimezone || "UTC";
  const { user } = useUserStore();
  const { setIsOpen, setDefaultDate, setDefaultTime, setAction } = useCalendarModalStore();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const hasBefore = useMemo(() => {
    const newDate = parse(dateRange.from, "yyyy-MM-dd", new Date());

    newDate.setDate(newDate.getDate() - 1);
    if (calendarData) {
      return isAfter(
        newDate,
        addDays(parse(calendarData.calendarStartsAt, "yyyy-MM-dd", new Date()), -1),
      );
    }
  }, [shownDays, dateRange, calendarData]);

  const hasAfter = useMemo(() => {
    if (calendarData) {
      const newDate = addDays(parse(dateRange.from, "yyyy-MM-dd", new Date()), shownDays);

      return isBefore(
        newDate,
        addDays(parse(calendarData.calendarEndsAt, "yyyy-MM-dd", new Date()), 1),
      );
    }
  }, [shownDays, dateRange, calendarData]);

  const navigateWeek = useCallback(
    (direction: "prev" | "next") => {
      const newDate = parse(dateRange.from, "yyyy-MM-dd", new Date());

      let newStartDate = addDays(newDate, direction === "next" ? shownDays : -shownDays);
      let newEndDate = addDays(newStartDate, shownDays - 1);

      if (calendarData && isAfter(newEndDate, calendarData.calendarEndsAt)) {
        newEndDate = parse(calendarData.calendarEndsAt, "yyyy-MM-dd", new Date());
      }
      if (calendarData && isBefore(newStartDate, calendarData.calendarStartsAt)) {
        newStartDate = parse(calendarData.calendarStartsAt, "yyyy-MM-dd", new Date());
      }

      setDateRange({
        from: format(newStartDate, "yyyy-MM-dd"),
        to: format(newEndDate, "yyyy-MM-dd"),
      });
    },
    [dateRange, redirect, shownDays],
  );

  const handleDayClick = (day: Date) => {
    if (shownDays === 7) {
      day = startOfWeek(day, { weekStartsOn: 1 });
    }
    const newStartDate = day;
    let newEndDate = addDays(day, shownDays - 1);

    if (calendarData && isAfter(newEndDate, calendarData.calendarEndsAt)) {
      newEndDate = parse(calendarData.calendarEndsAt, "yyyy-MM-dd", new Date());
    }

    setDateRange({
      from: format(newStartDate, "yyyy-MM-dd"),
      to: format(newEndDate, "yyyy-MM-dd"),
    });
  };

  const visibleDateRange = useMemo(() => {
    return {
      from: parse(dateRange.from, "yyyy-MM-dd", new Date()),
      to: addDays(parse(dateRange.from, "yyyy-MM-dd", new Date()), shownDays - 1),
    };
  }, [dateRange, shownDays]);

  // Create formatted date range display
  const dateRangeDisplay = useMemo(() => {
    const startDate = parse(dateRange.from, "yyyy-MM-dd", new Date());
    const endDate = addDays(startDate, shownDays - 1);
    const startMonth = startDate.getMonth();
    const endMonth = endDate.getMonth();

    if (startMonth !== endMonth) {
      // Same year, different months
      return (
        <div className="flex items-center">
          <span className="font-medium">
            {format(startDate, "d MMM", { locale: getDateFnsLocale(i18n.language) })}
          </span>
          <span className="mx-1">–</span>
          <span className="font-medium">
            {format(endDate, "d MMM", { locale: getDateFnsLocale(i18n.language) })}
          </span>
        </div>
      );
    } else {
      // Same month and year
      return (
        <div className="flex items-center">
          <span className="w-3 font-medium">
            {format(startDate, "d", { locale: getDateFnsLocale(i18n.language) })}
          </span>
          <span className="mx-2">–</span>
          <span className="w-3 font-medium">
            {format(endDate, "d", { locale: getDateFnsLocale(i18n.language) })}
          </span>
          <span className="ml-2">
            {format(startDate, "MMM", { locale: getDateFnsLocale(i18n.language) })}
          </span>
        </div>
      );
    }
  }, [dateRange.from, shownDays, i18n.language]);

  return (
    <header className="tablet:gap-2 flex h-14 max-h-14 shrink-0 items-center justify-between">
      <div className="mx-2 my-2 flex items-center justify-between">
        {isMobile && <SidebarTrigger />}
        {!isMobile && <WorkspaceSwitcher />}
      </div>
      <div className="tablet:p-4 flex w-full items-center justify-between">
        <div className="tablet:gap-4 flex w-full items-center">
          <div className="bg-background flex w-full items-center gap-2">
            {/* Calendar navigation buttons */}
            <CoachMarker
              description={t("help:coachMarkers.calendarNavigation.description", {
                defaultValue:
                  "Use these arrow buttons to navigate between different time periods in your calendar. You can move forward or backward to view different weeks or days.",
              })}
              id="calendar-navigation"
              placement="bottom"
              title={t("help:coachMarkers.calendarNavigation.title", {
                defaultValue: "Calendar Navigation",
              })}
            >
              <div className="flex gap-2">
                <Button
                  disabled={!hasBefore}
                  size="icon"
                  variant="outline"
                  onClick={() => navigateWeek("prev")}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="sr-only">{t("common:calendar.previousPeriod")}</span>
                </Button>
                <Button
                  disabled={!hasAfter}
                  size="icon"
                  variant="outline"
                  onClick={() => navigateWeek("next")}
                >
                  <ChevronRight className="h-4 w-4" />
                  <span className="sr-only">{t("common:calendar.nextPeriod")}</span>
                </Button>
              </div>
            </CoachMarker>
            {/* Enhanced date display */}
            {!isMobile && (
              <div className="flex items-center gap-2 rounded-md px-3 py-1.5 transition-colors">
                <CalendarIcon className="text-accent-foreground h-4 w-4" />
                <div className="text-sm font-medium">{dateRangeDisplay}</div>
              </div>
            )}
            {/* Calendar popover for tablet view and above */}
            <CoachMarker
              description={t("help:coachMarkers.calendarDateSelector.description", {
                defaultValue:
                  "Click this calendar icon to open a date picker and quickly jump to any specific date. This is useful when you need to navigate to a date far in the future or past.",
              })}
              id="calendar-date-selector"
              placement="bottom"
              title={t("help:coachMarkers.calendarDateSelector.title", {
                defaultValue: "Quick Date Selection",
              })}
            >
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={
                            "hover:bg-accent hover:text-accent-foreground ml-2 rounded-md p-2"
                          }
                        >
                          <CalendarIcon className="h-5 w-5" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <span>{t("common:calendar.openCalendar")}</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </PopoverTrigger>
                <PopoverContent
                  className="z-120 mt-2 flex w-fit items-center justify-center border-none p-4"
                  side={"bottom"}
                >
                  <DayPicker
                    className={""}
                    classNames={{
                      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                      month: "space-y-4",
                      caption:
                        "flex justify-center pt-1 relative items-center text-primary-foreground",
                      caption_label: "text-sm font-medium",
                      nav: "space-x-1 flex items-center",
                      nav_button: cn(
                        buttonVariants({ variant: "outline" }),
                        "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                      ),
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex",
                      head_cell: "text-muted-foreground w-8 font-normal text-[0.8rem]",
                      row: "flex w-full mt-2",
                      cell: "h-8 w-8 text-center text-sm p-0 relative rounded-0! focus-within:relative focus-within:z-20",
                      day: cn(
                        buttonVariants({ variant: "ghost" }),
                        "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-accent transition-all duration-300",
                      ),
                      day_range_end: "day-range-end rounded-e-xl!",
                      day_range_start: "rounded-s-xl!",
                      day_selected:
                        "bg-accent! text-primary-foreground hover:bg-primary! rounded-none! hover:text-primary-foreground ",
                      day_today: "bg-accent-foreground text-primary ",
                      day_outside:
                        "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
                      day_disabled: "text-muted-foreground opacity-50",
                      day_range_middle: "",
                      day_hidden: "invisible",
                    }}
                    components={{
                      IconLeft: () => <ChevronLeft className="h-4 w-4" />,
                      IconRight: () => <ChevronRight className="h-4 w-4" />,
                    }}
                    defaultMonth={parse(dateRange.from, "yyyy-MM-dd", new Date())}
                    fromDate={parse(calendarData?.calendarStartsAt ?? "", "yyyy-MM-dd", new Date())}
                    locale={getDateFnsLocale(i18n.language)}
                    modifiers={{
                      selected: visibleDateRange,
                      range_start: visibleDateRange.from,
                      range_end: visibleDateRange.to,
                    }}
                    showOutsideDays={true}
                    toDate={parse(calendarData?.calendarEndsAt ?? "", "yyyy-MM-dd", new Date())}
                    weekStartsOn={1}
                    onDayClick={(day: Date) => {
                      handleDayClick(day);
                      setIsCalendarOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </CoachMarker>
            {/* Add new slot button for tutors on tablet view and above */}
            {user?.role === "tutor" && (
              <div className="ms-auto flex items-center gap-2">
                <CoachMarker
                  description={t("help:coachMarkers.newSlotButton.description", {
                    defaultValue:
                      "Click this button to create a new lesson slot. You can set the date, time, subject, and price for your lesson. Students will be able to book these slots.",
                  })}
                  id="new-slot-button"
                  placement="bottom"
                  title={t("help:coachMarkers.newSlotButton.title", {
                    defaultValue: "Create New Lesson Slot",
                  })}
                >
                  <Button
                    className="text-xs sm:text-sm"
                    size={"sm"}
                    variant="foreground"
                    onClick={() => {
                      setAction("create");
                      setDefaultDate(format(new Date(), "yyyy-MM-dd"));
                      setDefaultTime(
                        format(
                          new Date(
                            new Date().setMinutes(
                              new Date().getMinutes() - (new Date().getMinutes() % 5) + 10,
                            ),
                          ),
                          "HH:mm",
                        ),
                      );
                      setIsOpen(true);
                    }}
                  >
                    <span className="iconify tabler--plus" />
                    <span className={"hidden sm:inline"}>{t("common:calendar.newSlot")}</span>
                  </Button>
                </CoachMarker>
                <CoachMarker
                  description={t("help:coachMarkers.templateApplyButton.description", {
                    defaultValue:
                      "Use this button to apply a pre-saved template to fill your calendar with standard lesson slots. This is a quick way to populate your schedule with your regular availability.",
                  })}
                  id="template-apply-button"
                  placement="bottom"
                  title={t("help:coachMarkers.templateApplyButton.title", {
                    defaultValue: "Apply Templates",
                  })}
                >
                  <TemplateApplyButton />
                </CoachMarker>
              </div>
            )}
          </div>
          <CoachMarker
            description={t("help:coachMarkers.timezoneDisplay.description", {
              defaultValue:
                "This shows the current timezone your calendar is displayed in. You can change this in your settings to match your location or preferred timezone.",
            })}
            id="timezone-display"
            placement="left"
            title={t("help:coachMarkers.timezoneDisplay.title", {
              defaultValue: "Timezone Display",
            })}
          >
            <Button className={"hidden h-9 min-[930px]:flex"} variant="ghost">
              <Globe className="h-4 w-4" />
              <p className="flex items-center gap-1">
                {getTimezoneAbbreviation(displayTimezone)}{" "}
                <Circle className="h-2! w-2!" fill={"#fff"} />{" "}
                {t(
                  //@ts-ignore
                  `timezones:${displayTimezone}`,
                )}
              </p>
            </Button>
          </CoachMarker>
        </div>
      </div>
    </header>
  );
}
